import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import { solutionIndustries, solutionTeams, customerStats, site } from '@/data/content';

const customerChallenges = [
  {
    icon: 'MessageSquareMore',
    title: 'Messages come from everywhere',
    text: 'Your customers talk to you on WhatsApp, Instagram, email, live chat, and X. Your team has to check five apps to find one answer.',
  },
  {
    icon: 'Timer',
    title: 'Slow replies kill trust',
    text: 'A lead that waits more than an hour is usually a lost lead. A customer waiting for support is already thinking about switching.',
  },
  {
    icon: 'UsersRound',
    title: 'Teams cannot scale',
    text: 'Hiring more agents is expensive. Your team spends more time switching tools than talking to customers.',
  },
  {
    icon: 'TrendingDown',
    title: 'Revenue leaks every day',
    text: 'Abandoned carts, missed quotes, and silent trial churn all happen because help is not fast enough.',
  },
];

const beforeAfter = {
  before: [
    { title: 'Five inboxes, five passwords', text: 'Email, WhatsApp, live chat, Instagram, and SMS each live in a different tool.' },
    { title: 'No shared history', text: 'Customers repeat themselves every time they switch channels or agents.' },
    { title: 'Manual follow-up', text: 'Escalations, reminders, and reviews are tracked in memory or spreadsheets.' },
  ],
  after: [
    { title: 'One inbox for every channel', text: 'All customer conversations flow into a single, shared workspace.' },
    { title: 'Full customer context', text: 'Past messages, orders, tickets, and notes are visible instantly.' },
    { title: 'AI does the busy work', text: 'Drafts, summaries, reminders, and review requests run automatically.' },
  ],
};

const whyNow = [
  { icon: 'Zap', title: 'Stop losing revenue', text: 'Faster replies, automated follow-ups, and proactive outreach turn more conversations into revenue.' },
  { icon: 'BadgeDollarSign', title: 'Replace five tools with one', text: 'One platform for chat, tickets, voice, knowledge base, and analytics. One price per agent.' },
  { icon: 'Sparkles', title: 'AI your team can trust', text: 'Zyra AI drafts replies from your content, not a black-box model, so answers are accurate and safe.' },
  { icon: 'Globe', title: 'Built for global teams', text: 'Run customer experience for Africa, Europe, North America, and Asia from one workspace.' },
];

export default function Solutions() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'ZuriHub Solutions',
      description: 'Customer experience solutions for industries and teams around the world.',
      url: `${site.baseUrl}/solutions`,
      hasPart: [
        ...solutionIndustries.map((i) => ({
          '@type': 'WebPage',
          name: i.metaTitle,
          url: `${site.baseUrl}/industry/${i.slug}`,
        })),
        ...solutionTeams.map((t) => ({
          '@type': 'WebPage',
          name: t.metaTitle,
          url: `${site.baseUrl}/solutions/${t.slug}`,
        })),
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Solutions"
        description="ZuriHub solutions for e-commerce, services, education, health, finance, SaaS, travel, customer service, and marketing & sales teams."
        path="/solutions"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-semibold text-slate-300 backdrop-blur">
            Solutions
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Fix the customer experience problems that <span className="gradient-text">cost you revenue</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            ZuriHub unites every customer channel, adds AI, and gives your team the context to turn conversations into conversions.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/why-zuri"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              See why ZuriHub <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="challenges-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="challenges-heading" className="section-title">Customer experience challenges we solve</h2>
            <p className="section-subtitle mx-auto">
              Every industry feels these problems. Click the one that sounds like you.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {customerChallenges.map((c) => (
              <div
                key={c.title}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-8 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <Icon name={c.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{c.title}</h3>
                <p className="mt-3 text-slate-600">{c.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {solutionIndustries.map((industry) => (
              <Link
                key={industry.slug}
                to={`/industry/${industry.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-700"
              >
                {industry.name} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="beforeafter-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="beforeafter-heading" className="section-title text-white">Before and after ZuriHub</h2>
            <p className="section-subtitle mx-auto text-slate-300">
              See what changes when your team stops switching tools and starts serving customers.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="text-xl font-bold text-slate-300">Without ZuriHub</h3>
              <div className="mt-6 space-y-5">
                {beforeAfter.before.map((b) => (
                  <div key={b.title} className="flex gap-4">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/20 text-red-400">
                      <X className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{b.title}</h4>
                      <p className="mt-1 text-sm text-slate-400">{b.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="text-xl font-bold text-white">With ZuriHub</h3>
              <div className="mt-6 space-y-5">
                {beforeAfter.after.map((a) => (
                  <div key={a.title} className="flex gap-4">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-400">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{a.title}</h4>
                      <p className="mt-1 text-sm text-slate-400">{a.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="why-now-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-now-heading" className="section-title">Why switch to ZuriHub now</h2>
            <p className="section-subtitle mx-auto">
              The fastest way to turn customer experience from a cost center into a growth engine.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whyNow.map((w) => (
              <div
                key={w.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <Icon name={w.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                <p className="mt-3 text-slate-600">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Solutions by industry</h2>
            <p className="section-subtitle mx-auto">
              See how ZuriHub solves the exact problems in your sector.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {solutionIndustries.map((industry) => (
              <Link
                key={industry.slug}
                to={`/industry/${industry.slug}`}
                className="group rounded-3xl border border-slate-100 bg-slate-50 p-8 transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner transition group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white">
                  <Icon name={industry.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{industry.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{industry.summary}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-bold text-brand-600 group-hover:gap-2 transition">
                  See the solution <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="teams-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="teams-heading" className="section-title">Solutions by team</h2>
            <p className="section-subtitle mx-auto">
              Built for the people who talk to customers every day.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {solutionTeams.map((team) => (
              <Link
                key={team.slug}
                to={`/solutions/${team.slug}`}
                className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner transition group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white">
                  <Icon name={team.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{team.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{team.summary}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-bold text-brand-600 group-hover:gap-2 transition">
                  See the solution <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="proof-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="proof-heading" className="section-title text-white">Proof that it works</h2>
            <p className="section-subtitle mx-auto text-slate-300">
              Results teams see after switching to ZuriHub.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {customerStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8 text-center"
              >
                <p className="text-5xl font-extrabold text-brand-400">{stat.value}</p>
                <p className="mt-2 text-lg font-semibold text-slate-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start turning conversations into revenue</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Get a 14-day free trial, connect your channels, and see why teams switch to ZuriHub.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
